import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Grid,
  Paper,
  Chip,
  IconButton,
  makeStyles,
  Divider,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline';
import StorageIcon from '@material-ui/icons/Storage';
import CloudDoneIcon from '@material-ui/icons/CloudDone';
import {
  getSyncStats,
  uploadBooksAndChapters,
  uploadHadithsBatch,
} from '../../services/hadithService';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 20,
    padding: theme.spacing(1),
    background: theme.palette.background.paper,
    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
    maxWidth: 750,
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  titleIcon: {
    color: '#10b981',
    fontSize: 28,
  },
  statCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
    textAlign: 'center',
  },
  statValue: {
    fontWeight: 800,
    fontSize: '1.4rem',
    color: '#10b981',
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginTop: 4,
  },
  actionCard: {
    padding: theme.spacing(2.5),
    borderRadius: 16,
    border: `1.5px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    '&:hover': {
      borderColor: '#10b981',
      boxShadow: '0 4px 16px rgba(16,185,129,0.1)',
    },
  },
  logBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    background: theme.palette.type === 'dark' ? '#0f172a' : '#1e293b',
    color: '#f8fafc',
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    maxHeight: 140,
    overflowY: 'auto',
    whiteSpace: 'pre-line',
  },
}));

export default function HadithSyncDialog({ open, onClose, onSyncComplete }) {
  const classes = useStyles();
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [inProgress, setInProgress] = useState(false);
  const [currentTask, setCurrentTask] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [logs, setLogs] = useState([]);
  const abortControllerRef = useRef(null);

  const appendLog = (msg) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [`[${timestamp}] ${msg}`, ...prev.slice(0, 40)]);
  };

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const data = await getSyncStats();
      setStats(data);
    } catch (e) {
      appendLog(`Error fetching stats: ${e.message}`);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchStats();
    } else {
      setLogs([]);
      setProgressPercent(0);
      setStatusMessage('');
      setInProgress(false);
    }
  }, [open]);

  const handleCancelOrPause = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      appendLog('Cancellation requested by user...');
      setStatusMessage('Stopping upload...');
    }
  };

  // Upload metadata: books & chapters
  const handleUploadMetadata = async () => {
    if (inProgress) return;
    setInProgress(true);
    setCurrentTask('metadata');
    setProgressPercent(0);
    setStatusMessage('Uploading Books and Chapters metadata...');
    appendLog('Starting Books & Chapters upload...');

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      await uploadBooksAndChapters({
        onProgress: (pct) => setProgressPercent(pct),
        onStatus: (msg) => {
          setStatusMessage(msg);
          appendLog(msg);
        },
        signal: controller.signal,
      });
      appendLog('✅ Books and Chapters metadata successfully uploaded to Firebase!');
      await fetchStats();
      if (onSyncComplete) onSyncComplete();
    } catch (e) {
      appendLog(`❌ Upload error: ${e.message}`);
      setStatusMessage(e.message);
    } finally {
      setInProgress(false);
      setCurrentTask('');
    }
  };

  // Upload Batch: Half 1 (50%)
  const handleUploadHalf1 = async () => {
    if (inProgress) return;
    setInProgress(true);
    setCurrentTask('half1');
    setProgressPercent(0);
    setStatusMessage('Starting Part 1 (First 50% / ~928 Hadiths)...');
    appendLog('Starting Part 1 upload (0% - 50%)...');

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      await uploadHadithsBatch({
        startRatio: 0,
        endRatio: 0.5,
        onProgress: (pct, count, totalBatch, globalIdx, globalTotal) => {
          setProgressPercent(pct);
          setStatusMessage(`Uploaded ${count} of ${totalBatch} (Global: ${globalIdx} / ${globalTotal})`);
        },
        onStatus: (msg) => appendLog(msg),
        signal: controller.signal,
      });
      appendLog('✅ Part 1 (50%) successfully uploaded! Half data is now safe in Firebase.');
      await fetchStats();
      if (onSyncComplete) onSyncComplete();
    } catch (e) {
      appendLog(`❌ Upload error: ${e.message}`);
      setStatusMessage(e.message);
    } finally {
      setInProgress(false);
      setCurrentTask('');
    }
  };

  // Upload Batch: Remaining (50%)
  const handleUploadRemaining = async () => {
    if (inProgress) return;
    setInProgress(true);
    setCurrentTask('remaining');
    setProgressPercent(0);
    setStatusMessage('Starting Part 2 (Remaining 50% / ~928 Hadiths)...');
    appendLog('Starting Part 2 upload (50% - 100%)...');

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      await uploadHadithsBatch({
        startRatio: 0.5,
        endRatio: 1.0,
        onProgress: (pct, count, totalBatch, globalIdx, globalTotal) => {
          setProgressPercent(pct);
          setStatusMessage(`Uploaded ${count} of ${totalBatch} (Global: ${globalIdx} / ${globalTotal})`);
        },
        onStatus: (msg) => appendLog(msg),
        signal: controller.signal,
      });
      appendLog('✅ Part 2 (Remaining 50%) successfully uploaded! All hadiths are now synced.');
      await fetchStats();
      if (onSyncComplete) onSyncComplete();
    } catch (e) {
      appendLog(`❌ Upload error: ${e.message}`);
      setStatusMessage(e.message);
    } finally {
      setInProgress(false);
      setCurrentTask('');
    }
  };

  // Upload All (100%)
  const handleUploadAll = async () => {
    if (inProgress) return;
    setInProgress(true);
    setCurrentTask('all');
    setProgressPercent(0);
    setStatusMessage('Starting 100% Full Upload...');
    appendLog('Starting 100% full upload of all Hadiths...');

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // Step 1: Metadata
      await uploadBooksAndChapters({
        onProgress: (pct) => setProgressPercent(Math.round(pct * 0.2)),
        onStatus: (msg) => appendLog(msg),
        signal: controller.signal,
      });

      // Step 2: Hadiths
      await uploadHadithsBatch({
        startRatio: 0,
        endRatio: 1.0,
        onProgress: (pct, count, totalBatch) => {
          const overallPct = Math.min(100, 20 + Math.round(pct * 0.8));
          setProgressPercent(overallPct);
          setStatusMessage(`Uploaded ${count} of ${totalBatch} Hadiths (${overallPct}% overall)`);
        },
        onStatus: (msg) => appendLog(msg),
        signal: controller.signal,
      });

      appendLog('🎉 100% Full Sync complete! Books, Chapters, and all Hadiths are safely in Firebase.');
      await fetchStats();
      if (onSyncComplete) onSyncComplete();
    } catch (e) {
      appendLog(`❌ Upload error: ${e.message}`);
      setStatusMessage(e.message);
    } finally {
      setInProgress(false);
      setCurrentTask('');
    }
  };

  const isHalfUploaded = stats?.meta?.halfUploaded || (stats?.cloud?.hadithsCount >= 900);
  const isFullUploaded = stats?.meta?.completed || (stats?.cloud?.hadithsCount >= 1850);

  return (
    <Dialog
      open={open}
      onClose={inProgress ? undefined : onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleLeft}>
          <CloudUploadIcon className={classes.titleIcon} />
          <Box>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              iHadis Firebase Cloud Uploader
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Safe quota-protected sync into new collections (<code style={{ color: '#10b981' }}>ihadis_hadiths</code>)
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small" onClick={fetchStats} disabled={inProgress || loadingStats} title="Refresh counts">
            <RefreshIcon />
          </IconButton>
          <IconButton size="small" onClick={onClose} disabled={inProgress}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: '20px 24px' }}>
        {/* Top Info Banner */}
        <Box
          p={2}
          mb={3}
          borderRadius={12}
          style={{
            background: 'rgba(16,185,129,0.08)',
            border: '1px solid rgba(16,185,129,0.25)',
          }}
        >
          <Typography variant="body2" style={{ color: '#047857', fontWeight: 500 }}>
            💡 <strong>Safe Multi-Part Upload:</strong> Firestore free tier limits writes to 20,000/day. You can safely upload <strong>Part 1 (First 50%)</strong> today, and upload <strong>Part 2 (Remaining 50%)</strong> tomorrow or whenever you choose. Data uses dedicated new collections with zero conflict!
          </Typography>
        </Box>

        {/* Sync Comparison Cards */}
        <Grid container spacing={2} style={{ marginBottom: 20 }}>
          <Grid item xs={4}>
            <Paper className={classes.statCard} elevation={0}>
              <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} mb={0.5}>
                <StorageIcon fontSize="small" style={{ color: '#64748b' }} />
                <Typography variant="caption" style={{ fontWeight: 600, color: '#64748b' }}>
                  Books
                </Typography>
              </Box>
              <Typography className={classes.statValue}>
                {stats?.cloud?.booksCount ?? 0} <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>/ {stats?.local?.booksCount || 25}</span>
              </Typography>
              <Typography className={classes.statLabel}>Uploaded to Cloud</Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper className={classes.statCard} elevation={0}>
              <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} mb={0.5}>
                <StorageIcon fontSize="small" style={{ color: '#64748b' }} />
                <Typography variant="caption" style={{ fontWeight: 600, color: '#64748b' }}>
                  Chapters
                </Typography>
              </Box>
              <Typography className={classes.statValue}>
                {stats?.cloud?.chaptersCount ?? 0} <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>/ {stats?.local?.chaptersCount || 654}</span>
              </Typography>
              <Typography className={classes.statLabel}>Uploaded to Cloud</Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper className={classes.statCard} elevation={0}>
              <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} mb={0.5}>
                <CloudDoneIcon fontSize="small" style={{ color: '#10b981' }} />
                <Typography variant="caption" style={{ fontWeight: 600, color: '#10b981' }}>
                  Hadiths
                </Typography>
              </Box>
              <Typography className={classes.statValue}>
                {stats?.cloud?.hadithsCount ?? 0} <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>/ {stats?.local?.hadithsCount || 1856}</span>
              </Typography>
              <Typography className={classes.statLabel}>
                {isFullUploaded ? '100% Synced' : isHalfUploaded ? '50% Synced' : 'Ready to Sync'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Progress Bar (Visible while syncing) */}
        {inProgress && (
          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#10b981' }}>
                {statusMessage}
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 700 }}>
                {progressPercent}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              style={{ height: 10, borderRadius: 5, backgroundColor: 'rgba(16,185,129,0.15)' }}
            />
          </Box>
        )}

        <Divider style={{ margin: '16px 0' }} />

        {/* Action Buttons Grid */}
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12 }}>
          Choose Upload Action:
        </Typography>

        <Grid container spacing={2}>
          {/* Action 1: Upload Metadata (Books & Chapters) */}
          <Grid item xs={12} sm={6}>
            <Paper className={classes.actionCard} elevation={0}>
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    1. Books & Chapters
                  </Typography>
                  {stats?.cloud?.chaptersCount >= 600 && (
                    <Chip size="small" icon={<CheckCircleIcon />} label="Uploaded" style={{ background: '#ecfdf5', color: '#059669' }} />
                  )}
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Uploads 25 Books and 654 Chapters metadata into Firebase first.
                </Typography>
              </Box>
              <Box mt={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={inProgress}
                  onClick={handleUploadMetadata}
                  style={{ borderRadius: 10, borderColor: '#10b981', color: '#10b981' }}
                >
                  Upload Books & Chapters
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Action 2: Upload Half 1 (50%) */}
          <Grid item xs={12} sm={6}>
            <Paper className={classes.actionCard} elevation={0}>
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    2. Part 1 (First 50%)
                  </Typography>
                  {isHalfUploaded && (
                    <Chip size="small" icon={<CheckCircleIcon />} label="Done" style={{ background: '#ecfdf5', color: '#059669' }} />
                  )}
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Uploads first 928 hadiths safely without consuming your daily write quota.
                </Typography>
              </Box>
              <Box mt={2}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={inProgress}
                  onClick={handleUploadHalf1}
                  style={{
                    borderRadius: 10,
                    background: '#10b981',
                    color: '#ffffff',
                    fontWeight: 600,
                  }}
                >
                  Upload Part 1 (50%)
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Action 3: Upload Part 2 (Remaining 50%) */}
          <Grid item xs={12} sm={6}>
            <Paper className={classes.actionCard} elevation={0}>
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    3. Part 2 (Remaining 50%)
                  </Typography>
                  {isFullUploaded && (
                    <Chip size="small" icon={<CheckCircleIcon />} label="Done" style={{ background: '#ecfdf5', color: '#059669' }} />
                  )}
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Uploads the remaining 928 hadiths (index 928 to 1856) to complete the dataset.
                </Typography>
              </Box>
              <Box mt={2}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={inProgress}
                  onClick={handleUploadRemaining}
                  style={{
                    borderRadius: 10,
                    background: '#059669',
                    color: '#ffffff',
                    fontWeight: 600,
                  }}
                >
                  Upload Part 2 (Remaining 50%)
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Action 4: Upload All Safely */}
          <Grid item xs={12} sm={6}>
            <Paper className={classes.actionCard} elevation={0}>
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    4. Full Upload (All 100%)
                  </Typography>
                  <Chip size="small" label="Full" style={{ background: '#f1f5f9', color: '#475569' }} />
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Sequentially uploads books, chapters, and all 1,856 hadiths with gentle pauses.
                </Typography>
              </Box>
              <Box mt={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={inProgress}
                  onClick={handleUploadAll}
                  style={{
                    borderRadius: 10,
                    borderColor: '#059669',
                    color: '#059669',
                    fontWeight: 600,
                  }}
                >
                  Upload All Safely
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Live Logs Console */}
        {logs.length > 0 && (
          <Box mt={3}>
            <Typography variant="caption" style={{ fontWeight: 700, color: '#64748b' }}>
              Execution Activity Log:
            </Typography>
            <Box className={classes.logBox}>{logs.join('\n')}</Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', borderTop: `1px solid rgba(0,0,0,0.08)` }}>
        {inProgress && (
          <Button
            startIcon={<PauseCircleOutlineIcon />}
            onClick={handleCancelOrPause}
            color="secondary"
            variant="outlined"
            style={{ borderRadius: 10 }}
          >
            Cancel / Pause Upload
          </Button>
        )}
        <Button onClick={onClose} disabled={inProgress} style={{ borderRadius: 10 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
